class DateOrNullOrUndefinedClass {
  value: unknown;

  constructor(value?: unknown) {
    this.value = value;
  }

  static from(value?: unknown): Date | undefined {
    if (value === undefined) return undefined;
    if (value === null) return undefined;

    // Handle string values that might be dates in Y-m-d format
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return parseYMDDate(value);
    }

    return new Date(value as string | number | Date);
  }
}

// Declarar la interfaz y el constructor
interface DateOrNullOrUndefinedConstructor {
  new (value?: unknown): DateOrNullOrUndefinedClass;
  (value?: unknown): Date | undefined;
  readonly prototype: DateOrNullOrUndefinedClass;
}

const DateOrNullOrUndefinedFunction = function (
  this: unknown,
  value?: unknown
) {
  if (this instanceof DateOrNullOrUndefinedFunction) {
    return new DateOrNullOrUndefinedClass(value);
  } else {
    return DateOrNullOrUndefinedClass.from(value);
  }
};

DateOrNullOrUndefinedFunction.prototype = DateOrNullOrUndefinedClass.prototype;
export const DateOrNullOrUndefined: DateOrNullOrUndefinedConstructor =
  DateOrNullOrUndefinedFunction as DateOrNullOrUndefinedConstructor;

/**
 * Parses a date string in Y-m-d format to create a Date object in the local timezone
 * This ensures dates without time components don't get shifted to the previous day
 * @param dateString Date string in YYYY-MM-DD format
 * @returns Date object correctly set to the specified date in local timezone
 */
export const parseYMDDate = (dateString: string): Date => {
  if (!dateString) return new Date(NaN);

  // Split the date string into components
  const [year, month, day] = dateString.split('-').map(Number);

  // Create a date using local timezone (year, monthIndex, day)
  // Note: JavaScript months are 0-indexed
  return new Date(year, month - 1, day);
};

/**
 * Converts a Date object to ISO string in the user's local timezone
 * @param date The Date object to convert
 * @returns ISO string in local timezone format (YYYY-MM-DDTHH:mm:ss.sssZ)
 */
export const toIsoStringInLocalTimezone = (date: Date): string => {
  if (!date) return '';

  // Get timezone offset in minutes and convert to milliseconds
  const tzOffset = date.getTimezoneOffset() * 60000;

  // Adjust the date by adding the timezone offset (which is negative for timezones ahead of UTC)
  const localDate = new Date(date.getTime() - tzOffset);

  // Return the ISO string
  return localDate.toISOString();
};

/**
 * Extracts the date portion from a Date object in the user's local timezone
 * @param date The Date object to convert
 * @returns Date string in YYYY-MM-DD format in local timezone
 */
export const fromIsoToDate = (date: Date): string => {
  if (!date) return '';
  return toIsoStringInLocalTimezone(date).split('T')[0];
};

/**
 * Formats a Date object as a YYYY-MM-DD string in the user's local timezone
 * @param value The Date object or date string to format (defaults to current date)
 * @returns Date string in YYYY-MM-DD format
 */
export const formatLocalYMD = (value: Date | string = new Date()): string => {
  if (!value) return '';

  let date: Date;
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }
    date = new Date(value);
  } else {
    date = value;
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Formats a Date object or a Y-m-d string to a localized date string
 * Ensures consistent date display across the application
 * @param value Date or string in YYYY-MM-DD format
 * @returns Formatted date string in user's locale
 */
export const formatLocalDate = (value: Date | string): string => {
  if (!value) return '';

  let date: Date;

  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    date = parseYMDDate(value);
  } else {
    date = value instanceof Date ? value : new Date(value);
  }

  return date.toLocaleDateString();
};
